import { debounce } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import { useMemo, useState, ChangeEvent, useCallback } from 'react';

import { useTheme } from '@mui/material/styles';
import { Stack, useMediaQuery } from '@mui/system';
import { Card, Table, TableBody, TableContainer } from '@mui/material';

import { useBoolean } from 'src/hooks/use-boolean';
import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';

import { useTranslate } from 'src/locales';
import { rootApi } from 'src/services/rootApi';
import { useAppDispatch } from 'src/store/store';
import { PageOptions } from 'src/services/api.types';
import { ContactLinkEntityType } from 'src/services/contacts/contacts.types';
import { ICompanyContactLink } from 'src/services/connections/connections.types';
import { useRemoveContactLinkMutation } from 'src/services/contacts/contacts.service';
import { useGetCompanyContactLinksQuery } from 'src/services/connections/connections.service';

import Scrollbar from 'src/components/scrollbar';
import {
  useTable,
  emptyRows,
  TableNoData,
  TableSkeleton,
  TableEmptyRows,
  TableHeadCustom,
  TablePaginationCustom,
} from 'src/components/table';

import AddContactModal from 'src/sections/contacts/modal/add-contact-modal';

import { TenantType } from 'src/types/enums';

import { ContactLinksTableRow } from './contact-links-table-row';
import ContactLinksTableToolbar from './contact-links-table-toolbar';
import { ContactEditorDialog } from '../../modal/contact-editor-dialog';

type Props = {
  profileId: string;
};

const DEFAULT_PAGE: PageOptions = {
  page: 1,
  per_page: 10,
};

export default function ContactLinksTable({
  profileId
}: Props) {

  const table = useTable();

  const theme = useTheme();

  const dispatch = useAppDispatch();

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const tenant = useOrgTenant();

  const { t } = useTranslate();

  const contactEditorControl = useBoolean(false);

  const addContactModalControl = useBoolean();

  const denseHeight = table.dense ? 56 : 56 + 20;

  const [pageOptions, setPageOptions] = useState<PageOptions>(DEFAULT_PAGE);
  const [filterQuery, setFilterQuery] = useState<string | undefined>();

  const { currentData: contactLinks, isLoading: isRetrievingContacts } = useGetCompanyContactLinksQuery({
    params: {
      ...pageOptions,
      search_query: filterQuery
    },
    profile_id: profileId as string
  });

  const [removeContactLink, { isLoading: isRemovingContactLink }] = useRemoveContactLinkMutation();

  const notFound = !contactLinks?.count;

  const isCandidate = tenant === TenantType.Candidate;

  const tableLabels = useMemo(
    () => [
      { id: 'name', label: t('contacts.table.name') },
      { id: 'email', label: t('contacts.table.email') },
      { id: 'phone', label: t('contacts.table.phone') },
      { id: 'source', label: t('contacts.table.source') },
      { id: 'type', label: t('contacts.table.type') },
      { id: 'notes', label: '' }
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isCandidate]
  );

  const handleFilterChange = useMemo(
    () => debounce((filter, value: string | TenantType) => {
      if (filter === 'search_query') {
        setFilterQuery(value as string);
      }
    }, 500),
    [setFilterQuery]
  );

  const clearFilters = useCallback(() => {
    setFilterQuery('');
  }, []);

  const onChangePage = useCallback((_event: any, pageNumber: number) => {
    setPageOptions((prev) => ({
      ...prev,
      page: pageNumber + 1,
    }));
  }, []);

  const onChangeRowsPerPage = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setPageOptions((prev) => ({
        ...prev,
        per_page: +event.target.value,
      }));
    },
    []
  );

  const handleInitiateLinkContact = useCallback(() => {
    contactEditorControl.onTrue();
  }, [contactEditorControl]);

  const handleRemoveContact = useCallback(async (contactLink: ICompanyContactLink) => {
    try {
      await removeContactLink({
        link_id: contactLink.id,
        entity_id: profileId,
        entity_type: ContactLinkEntityType.COMPANY_PROFILE
      }).unwrap();

      enqueueSnackbar(t('companies.profile.api.remove_contact_link.success'), { variant: 'success' });

    }
    catch (error) {
      enqueueSnackbar(t('companies.profile.api.remove_contact_link.default_error'), { variant: 'error' });
    }

  }, [profileId, removeContactLink, t]);

  const openAddContactModal = useCallback(() => {
    addContactModalControl.onTrue();
  }, [addContactModalControl]);

  const onNewContactAdded = useCallback(() => {
    contactEditorControl.onFalse();
    addContactModalControl.onFalse();
    dispatch(rootApi.util.invalidateTags([{ type: 'CompanyContactLink', id: profileId }, { type: 'CompanyContacts', id: profileId }]))
  }, [addContactModalControl, dispatch, profileId, contactEditorControl]);

  return (
    <Card>
      {
        isRetrievingContacts ? (
          <TableSkeleton />
        )
          :
          <>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <ContactLinksTableToolbar
                onAddContact={handleInitiateLinkContact}
                onFilters={handleFilterChange}
                clearFilters={clearFilters}
              />
            </Stack>

            <TableContainer sx={{ overflow: 'unset' }}>
              <Scrollbar>
                <Table size={table.dense ? 'small' : 'medium'}>
                  {
                    !isMobile && (
                      <TableHeadCustom headLabel={tableLabels} />
                    )
                  }

                  <TableBody>
                    {contactLinks?.results?.map((row) => <ContactLinksTableRow key={row.id} row={row} onRemoveLink={handleRemoveContact} />)}

                    <TableEmptyRows
                      height={denseHeight}
                      emptyRows={emptyRows(table.page, table.rowsPerPage, contactLinks?.count ?? 0)}
                    />

                    <TableNoData notFound={notFound} />
                  </TableBody>
                </Table>
              </Scrollbar>
            </TableContainer>

            <TablePaginationCustom
              count={contactLinks?.count ?? 0}
              page={pageOptions.page}
              rowsPerPage={pageOptions.per_page}
              onPageChange={onChangePage}
              onRowsPerPageChange={onChangeRowsPerPage}
              dense={table.dense}
              onChangeDense={table.onChangeDense}
            />
          </>
      }

      {
        contactEditorControl.value && (
          <ContactEditorDialog
            open={contactEditorControl.value}
            onClose={contactEditorControl.onFalse}
            profileId={profileId as string}
            onAddContact={openAddContactModal}
          />
        )
      }

      <AddContactModal open={addContactModalControl.value} onClose={addContactModalControl.onFalse} companyId={profileId} onSuccess={onNewContactAdded} />
    </Card>
  );
}
